/**
 * Shared utility for parsing follow-up questions and choices from AI assistant messages.
 * Questions are identified by lines starting with → (arrow) character.
 * Choices are identified by "→ choice:" blocks with bullet point options.
 * This is used by both the copilot widget and admin analytics views.
 */

export interface ParsedContent {
  questions: string[];
  cleanedContent: string;
  choices: ChoiceBlock[];
}

/**
 * A choice block with an optional ID and list of clickable options.
 */
export interface ChoiceBlock {
  id?: string; // Optional identifier like "category_decision"
  options: string[];
}

/**
 * Parse follow-up questions (lines starting with →) and choice blocks from content.
 * Questions are plain text suggestions, choices are structured clickable options.
 *
 * @param content - The raw message content from the AI assistant
 * @returns Object containing extracted questions, choices, and cleaned content
 */
export const parseQuestionsAndCleanContent = (
  content: string,
): ParsedContent => {
  const questions: string[] = [];
  const choices: ChoiceBlock[] = [];
  const linesToRemove: string[] = [];

  // First, find and extract choice blocks (→ choice: followed by bullet points)
  // Handle optional bold formatting: **→ choice:** or → choice: or → **choice:**
  const choiceBlockRegex =
    /(?:\*\*)?→\s*(?:\*\*)?choice:?\s*(\w*)?(?:\*\*)?\n((?:\s*[-•]\s*.+\n?)+)/gi;
  let choiceMatch;
  const choiceBlocksToRemove: string[] = [];

  while ((choiceMatch = choiceBlockRegex.exec(content)) !== null) {
    const id = choiceMatch[1]?.trim() || undefined;
    const optionsText = choiceMatch[2];

    // Parse individual options from bullet points
    const optionRegex = /[-•]\s*(.+?)(?:\n|$)/g;
    const options: string[] = [];
    let optionMatch;
    while ((optionMatch = optionRegex.exec(optionsText)) !== null) {
      const option = optionMatch[1].trim();
      if (option) {
        options.push(option);
      }
    }

    if (options.length > 0) {
      choices.push({ id, options });
      choiceBlocksToRemove.push(choiceMatch[0]);
    }
  }

  // Remove choice blocks from content
  let cleanedContent = content;
  choiceBlocksToRemove.forEach((block) => {
    cleanedContent = cleanedContent.replace(block, "");
  });

  // Then parse regular questions (→ followed by plain text, not choice blocks)
  const questionRegex = /^→\s+(.+?)(?:\n|$)/gm;
  let match;

  while ((match = questionRegex.exec(cleanedContent)) !== null) {
    const questionText = match[1].trim();

    // Skip if it's a link (handled by MessageContent) or a choice marker
    // Also skip bold-wrapped choice (e.g., **choice: ...**) — strip leading * before check
    if (
      !questionText.includes("[") &&
      !questionText.includes("](") &&
      !questionText.replace(/^\*+/, "").toLowerCase().startsWith("choice")
    ) {
      // Skip headers that end with colon (these introduce bullet point lists, not clickable questions)
      // Examples: "**Should I:**", "Should I treat these as:", "**Options:**"
      if (questionText.endsWith(":") || questionText.endsWith(":**")) {
        continue;
      }
      questions.push(questionText);
      linesToRemove.push(match[0]);
    }
  }

  // Remove only non-link question lines from content (they'll appear in question box)
  linesToRemove.forEach((line) => {
    cleanedContent = cleanedContent.replace(line, "");
  });

  // Clean up any trailing "Questions to consider:" header if all questions removed
  cleanedContent = cleanedContent.replace(
    /\n*\*\*Questions to consider:\*\*\s*$/g,
    "",
  );
  cleanedContent = cleanedContent.trim();

  return { questions, cleanedContent, choices };
};
